// Browsers may implement Math.sin, Math.atan2, ... with any approximation, and
// they do differ: Safari and Chrome disagree on about 4% of Math.sin results.
// Replays must simulate bit-identically everywhere, so these are ports of
// fdlibm that only use +, -, *, / and Math.sqrt, which IEEE 754 fixes exactly.
// vite.config.mjs routes every Math.sin & co in src/ to this module.
const buffer = new ArrayBuffer(8);
const f64 = new Float64Array(buffer);
const i32 = new Int32Array(buffer);
const u32 = new Uint32Array(buffer);
const HI = new Uint8Array(new Uint32Array([1]).buffer)[0] === 1 ? 1 : 0;
const LO = 1 - HI;

const getHigh = x => { f64[0] = x; return i32[HI]; };
const getLow = x => { f64[0] = x; return u32[LO]; };
const setHigh = (x, high) => { f64[0] = x; i32[HI] = high; return f64[0]; };
const setLow = (x, low) => { f64[0] = x; u32[LO] = low; return f64[0]; };
const fromWords = (high, low) => { i32[HI] = high; u32[LO] = low; return f64[0]; };

const PI = 3.14159265358979311600e+00;
const PIO2_HI = 1.57079632679489655800e+00;
const PIO2_LO = 6.12323399573676603587e-17;
const PIO4_HI = 7.85398163397448278999e-01;
const PIO4_LO = 3.06161699786838301793e-17;
const TWO_PI = 6.283185307179586;

// Argument reduction: x = n * pi/2 + (reducedHigh + reducedLow)
const INVPIO2 = 6.36619772367581382433e-01;
const PIO2_1 = 1.57079632673412561417e+00;
const PIO2_1T = 6.07710050650619224932e-11;
const PIO2_2 = 6.07710050630396597660e-11;
const PIO2_2T = 2.02226624879595063154e-21;
const PIO2_3 = 2.02226624871116645580e-21;
const PIO2_3T = 8.47842766036889956997e-32;
let reducedHigh = 0;
let reducedLow = 0;

const remPio2 = x => {
	const hx = getHigh(x);
	const ix = hx & 0x7fffffff;
	if(ix <= 0x3fe921fb){
		reducedHigh = x;
		reducedLow = 0;
		return 0;
	}
	if(ix < 0x4002d97c){
		if(hx > 0){
			let z = x - PIO2_1;
			if(ix !== 0x3ff921fb){
				reducedHigh = z - PIO2_1T;
				reducedLow = (z - reducedHigh) - PIO2_1T;
			}else{
				z -= PIO2_2;
				reducedHigh = z - PIO2_2T;
				reducedLow = (z - reducedHigh) - PIO2_2T;
			}
			return 1;
		}
		let z = x + PIO2_1;
		if(ix !== 0x3ff921fb){
			reducedHigh = z + PIO2_1T;
			reducedLow = (z - reducedHigh) + PIO2_1T;
		}else{
			z += PIO2_2;
			reducedHigh = z + PIO2_2T;
			reducedLow = (z - reducedHigh) + PIO2_2T;
		}
		return -1;
	}
	// Beyond 2^19 * pi/2 fdlibm needs a 1200 bit table. No angle in the game
	// gets there; fold with the exact % so the result stays deterministic.
	if(ix > 0x413921fb) return remPio2(x % TWO_PI);

	const t = Math.abs(x);
	const n = (t * INVPIO2 + 0.5) | 0;
	const fn = n;
	let r = t - fn * PIO2_1;
	let w = fn * PIO2_1T;
	const j = ix >> 20;
	reducedHigh = r - w;
	let i = j - ((getHigh(reducedHigh) >> 20) & 0x7ff);
	if(i > 16){
		let t2 = r;
		w = fn * PIO2_2;
		r = t2 - w;
		w = fn * PIO2_2T - ((t2 - r) - w);
		reducedHigh = r - w;
		i = j - ((getHigh(reducedHigh) >> 20) & 0x7ff);
		if(i > 49){
			t2 = r;
			w = fn * PIO2_3;
			r = t2 - w;
			w = fn * PIO2_3T - ((t2 - r) - w);
			reducedHigh = r - w;
		}
	}
	reducedLow = (r - reducedHigh) - w;
	if(hx < 0){
		reducedHigh = -reducedHigh;
		reducedLow = -reducedLow;
		return -n;
	}
	return n;
};

const S1 = -1.66666666666666324348e-01;
const S2 = 8.33333333332248946124e-03;
const S3 = -1.98412698298579493134e-04;
const S4 = 2.75573137070700676789e-06;
const S5 = -2.50507602534068634195e-08;
const S6 = 1.58969099521155010221e-10;

const kernelSin = (x, y, iy) => {
	if((getHigh(x) & 0x7fffffff) < 0x3e400000) return x;
	const z = x * x;
	const v = z * x;
	const r = S2 + z * (S3 + z * (S4 + z * (S5 + z * S6)));
	if(iy === 0) return x + v * (S1 + z * r);
	return x - ((z * (0.5 * y - v * r) - y) - v * S1);
};

const C1 = 4.16666666666666019037e-02;
const C2 = -1.38888888888741095749e-03;
const C3 = 2.48015872894767294178e-05;
const C4 = -2.75573143513906633035e-07;
const C5 = 2.08757232129817482790e-09;
const C6 = -1.13596475577881948265e-11;

const kernelCos = (x, y) => {
	const ix = getHigh(x) & 0x7fffffff;
	if(ix < 0x3e400000) return 1;
	const z = x * x;
	const r = z * (C1 + z * (C2 + z * (C3 + z * (C4 + z * (C5 + z * C6)))));
	if(ix < 0x3fd33333) return 1 - (0.5 * z - (z * r - x * y));
	const qx = ix > 0x3fe90000 ? 0.28125 : fromWords(ix - 0x00200000, 0);
	const hz = 0.5 * z - qx;
	const a = 1 - qx;
	return a - (hz - (z * r - x * y));
};

const T0 = 3.33333333333334091986e-01;
const T1 = 1.33333333333201242699e-01;
const T2 = 5.39682539762260521377e-02;
const T3 = 2.18694882948595424599e-02;
const T4 = 8.86323982359930005737e-03;
const T5 = 3.59207910759131235356e-03;
const T6 = 1.45620945432529025516e-03;
const T7 = 5.88041240820264096874e-04;
const T8 = 2.46463134818469906812e-04;
const T9 = 7.81794442939557092300e-05;
const T10 = 7.14072491382608190305e-05;
const T11 = -1.85586374855275456654e-05;
const T12 = 2.59073051863633712884e-05;

const kernelTan = (x, y, iy) => {
	const hx = getHigh(x);
	const ix = hx & 0x7fffffff;
	if(ix < 0x3e300000){
		if(((ix | getLow(x)) | (iy + 1)) === 0) return 1 / Math.abs(x);
		if(iy === 1) return x;
		const w = x + y;
		const z = setLow(w, 0);
		const v = y - (z - x);
		const a = -1 / w;
		const t = setLow(a, 0);
		const s = 1 + t * z;
		return t + a * (s + t * v);
	}
	const big = ix >= 0x3fe59428;
	if(big){
		if(hx < 0){
			x = -x;
			y = -y;
		}
		x = (PIO4_HI - x) + (PIO4_LO - y);
		y = 0;
	}
	let z = x * x;
	let w = z * z;
	let r = T1 + w * (T3 + w * (T5 + w * (T7 + w * (T9 + w * T11))));
	let v = z * (T2 + w * (T4 + w * (T6 + w * (T8 + w * (T10 + w * T12)))));
	const s = z * x;
	r = y + z * (s * (r + v) + y);
	r += T0 * s;
	w = x + r;
	if(big){
		v = iy;
		return (1 - ((hx >> 30) & 2)) * (v - 2 * (x - (w * w / (w + v) - r)));
	}
	if(iy === 1) return w;
	z = setLow(w, 0);
	v = r - (z - x);
	const a = -1 / w;
	const t = setLow(a, 0);
	const s2 = 1 + t * z;
	return t + a * (s2 + t * v);
};

export function sin(x){
	x = +x;
	const ix = getHigh(x) & 0x7fffffff;
	if(ix <= 0x3fe921fb) return kernelSin(x, 0, 0);
	if(ix >= 0x7ff00000) return NaN;
	switch(remPio2(x) & 3){
		case 0: return kernelSin(reducedHigh, reducedLow, 1);
		case 1: return kernelCos(reducedHigh, reducedLow);
		case 2: return -kernelSin(reducedHigh, reducedLow, 1);
		default: return -kernelCos(reducedHigh, reducedLow);
	}
}

export function cos(x){
	x = +x;
	const ix = getHigh(x) & 0x7fffffff;
	if(ix <= 0x3fe921fb) return kernelCos(x, 0);
	if(ix >= 0x7ff00000) return NaN;
	switch(remPio2(x) & 3){
		case 0: return kernelCos(reducedHigh, reducedLow);
		case 1: return -kernelSin(reducedHigh, reducedLow, 1);
		case 2: return -kernelCos(reducedHigh, reducedLow);
		default: return kernelSin(reducedHigh, reducedLow, 1);
	}
}

export function tan(x){
	x = +x;
	const ix = getHigh(x) & 0x7fffffff;
	if(ix <= 0x3fe921fb) return kernelTan(x, 0, 1);
	if(ix >= 0x7ff00000) return NaN;
	const n = remPio2(x);
	return kernelTan(reducedHigh, reducedLow, 1 - ((n & 1) << 1));
}

const ATAN_HI = [4.63647609000806093515e-01, 7.85398163397448278999e-01, 9.82793723247329054082e-01, 1.57079632679489655800e+00];
const ATAN_LO = [2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17, 6.12323399573676603587e-17];
const AT0 = 3.33333333333329318027e-01;
const AT1 = -1.99999999998764832476e-01;
const AT2 = 1.42857142725034663711e-01;
const AT3 = -1.11111104054623557880e-01;
const AT4 = 9.09088713343650656196e-02;
const AT5 = -7.69187620504482999495e-02;
const AT6 = 6.66107313738753120669e-02;
const AT7 = -5.83357013379057348645e-02;
const AT8 = 4.97687799461593236017e-02;
const AT9 = -3.65315727442169155270e-02;
const AT10 = 1.62858201153657823623e-02;

export function atan(x){
	x = +x;
	const hx = getHigh(x);
	const ix = hx & 0x7fffffff;
	let id;
	if(ix >= 0x44100000){
		if(ix > 0x7ff00000 || (ix === 0x7ff00000 && getLow(x) !== 0)) return NaN;
		return hx > 0 ? ATAN_HI[3] + ATAN_LO[3] : -ATAN_HI[3] - ATAN_LO[3];
	}
	if(ix < 0x3fdc0000){
		if(ix < 0x3e400000) return x;
		id = -1;
	}else{
		x = Math.abs(x);
		if(ix < 0x3ff30000){
			if(ix < 0x3fe60000){
				id = 0;
				x = (2 * x - 1) / (2 + x);
			}else{
				id = 1;
				x = (x - 1) / (x + 1);
			}
		}else if(ix < 0x40038000){
			id = 2;
			x = (x - 1.5) / (1 + 1.5 * x);
		}else{
			id = 3;
			x = -1 / x;
		}
	}
	const z = x * x;
	const w = z * z;
	const s1 = z * (AT0 + w * (AT2 + w * (AT4 + w * (AT6 + w * (AT8 + w * AT10)))));
	const s2 = w * (AT1 + w * (AT3 + w * (AT5 + w * (AT7 + w * AT9))));
	if(id < 0) return x - x * (s1 + s2);
	const result = ATAN_HI[id] - ((x * (s1 + s2) - ATAN_LO[id]) - x);
	return hx < 0 ? -result : result;
}

const PI_O_4 = 7.8539816339744827900E-01;
const PI_LO = 1.2246467991473531772E-16;

export function atan2(y, x){
	y = +y;
	x = +x;
	const hx = getHigh(x);
	const ix = hx & 0x7fffffff;
	const lx = getLow(x);
	const hy = getHigh(y);
	const iy = hy & 0x7fffffff;
	const ly = getLow(y);
	if((ix | (lx !== 0 ? 1 : 0)) > 0x7ff00000 || (iy | (ly !== 0 ? 1 : 0)) > 0x7ff00000) return NaN;
	if(((hx - 0x3ff00000) | lx) === 0) return atan(y);
	let m = ((hy >> 31) & 1) | ((hx >> 30) & 2);
	if((iy | ly) === 0){
		switch(m){
			case 0:
			case 1: return y;
			case 2: return PI;
			default: return -PI;
		}
	}
	if((ix | lx) === 0) return hy < 0 ? -PIO2_HI : PIO2_HI;
	if(ix === 0x7ff00000){
		if(iy === 0x7ff00000){
			switch(m){
				case 0: return PI_O_4;
				case 1: return -PI_O_4;
				case 2: return 3 * PI_O_4;
				default: return -3 * PI_O_4;
			}
		}
		switch(m){
			case 0: return 0;
			case 1: return -0;
			case 2: return PI;
			default: return -PI;
		}
	}
	if(iy === 0x7ff00000) return hy < 0 ? -PIO2_HI : PIO2_HI;
	const k = (iy - ix) >> 20;
	let z;
	if(k > 60){
		z = PIO2_HI + 0.5 * PI_LO;
		m &= 1;
	}else if(hx < 0 && k < -60){
		z = 0;
	}else{
		z = atan(Math.abs(y / x));
	}
	switch(m){
		case 0: return z;
		case 1: return -z;
		case 2: return PI - (z - PI_LO);
		default: return (z - PI_LO) - PI;
	}
}

const PS0 = 1.66666666666666657415e-01;
const PS1 = -3.25565818622400915405e-01;
const PS2 = 2.01212532134862925881e-01;
const PS3 = -4.00555345006794114027e-02;
const PS4 = 7.91534994289814532176e-04;
const PS5 = 3.47933107596021167570e-05;
const QS1 = -2.40339491173441421878e+00;
const QS2 = 2.02094576023350569471e+00;
const QS3 = -6.88283971605453293030e-01;
const QS4 = 7.70381505559019352791e-02;

export function asin(x){
	x = +x;
	const hx = getHigh(x);
	const ix = hx & 0x7fffffff;
	if(ix >= 0x3ff00000){
		if(((ix - 0x3ff00000) | getLow(x)) === 0) return x * PIO2_HI + x * PIO2_LO;
		return NaN;
	}
	if(ix < 0x3fe00000){
		if(ix < 0x3e400000) return x;
		const t = x * x;
		const p = t * (PS0 + t * (PS1 + t * (PS2 + t * (PS3 + t * (PS4 + t * PS5)))));
		const q = 1 + t * (QS1 + t * (QS2 + t * (QS3 + t * QS4)));
		return x + x * (p / q);
	}
	const w = 1 - Math.abs(x);
	let t = w * 0.5;
	let p = t * (PS0 + t * (PS1 + t * (PS2 + t * (PS3 + t * (PS4 + t * PS5)))));
	let q = 1 + t * (QS1 + t * (QS2 + t * (QS3 + t * QS4)));
	const s = Math.sqrt(t);
	if(ix >= 0x3fef3333){
		t = PIO2_HI - (2 * (s + s * (p / q)) - PIO2_LO);
	}else{
		const sHigh = setLow(s, 0);
		const c = (t - sHigh * sHigh) / (s + sHigh);
		const r = p / q;
		p = 2 * s * r - (PIO2_LO - 2 * c);
		q = PIO4_HI - 2 * sHigh;
		t = PIO4_HI - (p - q);
	}
	return hx > 0 ? t : -t;
}

export function acos(x){
	x = +x;
	const hx = getHigh(x);
	const ix = hx & 0x7fffffff;
	if(ix >= 0x3ff00000){
		if(((ix - 0x3ff00000) | getLow(x)) === 0) return hx > 0 ? 0 : PI + 2 * PIO2_LO;
		return NaN;
	}
	if(ix < 0x3fe00000){
		if(ix <= 0x3c600000) return PIO2_HI + PIO2_LO;
		const z = x * x;
		const p = z * (PS0 + z * (PS1 + z * (PS2 + z * (PS3 + z * (PS4 + z * PS5)))));
		const q = 1 + z * (QS1 + z * (QS2 + z * (QS3 + z * QS4)));
		return PIO2_HI - (x - (PIO2_LO - x * (p / q)));
	}
	if(hx < 0){
		const z = (1 + x) * 0.5;
		const p = z * (PS0 + z * (PS1 + z * (PS2 + z * (PS3 + z * (PS4 + z * PS5)))));
		const q = 1 + z * (QS1 + z * (QS2 + z * (QS3 + z * QS4)));
		const s = Math.sqrt(z);
		const w = (p / q) * s - PIO2_LO;
		return PI - 2 * (s + w);
	}
	const z = (1 - x) * 0.5;
	const s = Math.sqrt(z);
	const df = setLow(s, 0);
	const c = (z - df * df) / (s + df);
	const p = z * (PS0 + z * (PS1 + z * (PS2 + z * (PS3 + z * (PS4 + z * PS5)))));
	const q = 1 + z * (QS1 + z * (QS2 + z * (QS3 + z * QS4)));
	const w = (p / q) * s + c;
	return 2 * (df + w);
}

const LN2_HI = 6.93147180369123816490e-01;
const LN2_LO = 1.90821492927058770002e-10;
const INVLN2 = 1.44269504088896338700e+00;
const P1 = 1.66666666666666019037e-01;
const P2 = -2.77777777770155933842e-03;
const P3 = 6.61375632143793436117e-05;
const P4 = -1.65339022054652515390e-06;
const P5 = 4.13813679705723846039e-08;
const TWOM1000 = 9.33263618503218878990e-302;
const TWO1023 = 8.98846567431158e307;

export function exp(x){
	x = +x;
	let hx = getHigh(x);
	const negative = hx < 0;
	hx &= 0x7fffffff;
	if(hx >= 0x40862E42){
		if(hx >= 0x7ff00000){
			if(((hx & 0xfffff) | getLow(x)) !== 0) return NaN;
			return negative ? 0 : x;
		}
		if(x > 7.09782712893383973096e+02) return Infinity;
		if(x < -7.45133219101941108420e+02) return 0;
	}
	let hi = 0;
	let lo = 0;
	let k = 0;
	if(hx > 0x3fd62e42){
		if(hx < 0x3FF0A2B2){
			hi = negative ? x + LN2_HI : x - LN2_HI;
			lo = negative ? -LN2_LO : LN2_LO;
			k = negative ? -1 : 1;
		}else{
			k = (INVLN2 * x + (negative ? -0.5 : 0.5)) | 0;
			hi = x - k * LN2_HI;
			lo = k * LN2_LO;
		}
		x = hi - lo;
	}else if(hx < 0x3e300000){
		return 1 + x;
	}
	const t = x * x;
	const c = x - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
	if(k === 0) return 1 - ((x * c) / (c - 2) - x);
	const y = 1 - ((lo - (x * c) / (2 - c)) - hi);
	if(k >= -1021){
		if(k === 1024) return y * 2 * TWO1023;
		return y * fromWords(0x3ff00000 + (k << 20), 0);
	}
	return y * fromWords(0x3ff00000 + ((k + 1000) << 20), 0) * TWOM1000;
}

const TWO54 = 1.80143985094819840000e+16;
const LG1 = 6.666666666666735130e-01;
const LG2 = 3.999999999940941908e-01;
const LG3 = 2.857142874366239149e-01;
const LG4 = 2.222219843214978396e-01;
const LG5 = 1.818357216161805012e-01;
const LG6 = 1.531383769920937332e-01;
const LG7 = 1.479819860511658591e-01;

export function log(x){
	x = +x;
	let hx = getHigh(x);
	const lx = getLow(x);
	let k = 0;
	if(hx < 0x00100000){
		if(((hx & 0x7fffffff) | lx) === 0) return -Infinity;
		if(hx < 0) return NaN;
		k -= 54;
		x *= TWO54;
		hx = getHigh(x);
	}
	if(hx >= 0x7ff00000) return x + x;
	k += (hx >> 20) - 1023;
	hx &= 0x000fffff;
	let i = (hx + 0x95f64) & 0x100000;
	x = setHigh(x, hx | (i ^ 0x3ff00000));
	k += i >> 20;
	const f = x - 1;
	const dk = k;
	if((0x000fffff & (2 + hx)) < 3){
		if(f === 0) return k === 0 ? 0 : dk * LN2_HI + dk * LN2_LO;
		const R = f * f * (0.5 - 0.33333333333333333 * f);
		if(k === 0) return f - R;
		return dk * LN2_HI - ((R - dk * LN2_LO) - f);
	}
	const s = f / (2 + f);
	const z = s * s;
	i = hx - 0x6147a;
	const w = z * z;
	const j = 0x6b851 - hx;
	const t1 = w * (LG2 + w * (LG4 + w * LG6));
	const t2 = z * (LG1 + w * (LG3 + w * (LG5 + w * LG7)));
	i |= j;
	const R = t2 + t1;
	if(i > 0){
		const hfsq = 0.5 * f * f;
		if(k === 0) return f - (hfsq - s * (hfsq + R));
		return dk * LN2_HI - ((hfsq - (s * (hfsq + R) + dk * LN2_LO)) - f);
	}
	if(k === 0) return f - s * (f - R);
	return dk * LN2_HI - ((s * (f - R) - dk * LN2_LO) - f);
}

const IVLN10 = 4.34294481903251816668e-01;
const LOG10_2HI = 3.01029995663611771306e-01;
const LOG10_2LO = 3.69423907715893078616e-13;

export function log10(x){
	x = +x;
	let hx = getHigh(x);
	const lx = getLow(x);
	let k = 0;
	if(hx < 0x00100000){
		if(((hx & 0x7fffffff) | lx) === 0) return -Infinity;
		if(hx < 0) return NaN;
		k -= 54;
		x *= TWO54;
		hx = getHigh(x);
	}
	if(hx >= 0x7ff00000) return x + x;
	k += (hx >> 20) - 1023;
	const i = (k >>> 31);
	hx = (hx & 0x000fffff) | ((0x3ff - i) << 20);
	const y = k + i;
	x = setHigh(x, hx);
	const z = y * LOG10_2LO + IVLN10 * log(x);
	return z + y * LOG10_2HI;
}

const BP = [1, 1.5];
const DP_H = [0, 5.84962487220764160156e-01];
const DP_L = [0, 1.35003920212974897128e-08];
const TWO53 = 9007199254740992;
const L1 = 5.99999999999994648725e-01;
const L2 = 4.28571428578550184252e-01;
const L3 = 3.33333329818377432918e-01;
const L4 = 2.72728123808534006489e-01;
const L5 = 2.30660745775561754067e-01;
const L6 = 2.06975017800338417784e-01;
const LG2_FULL = 6.93147180559945286227e-01;
const LG2_H = 6.93147182464599609375e-01;
const LG2_L = -1.90465429995776804525e-09;
const OVT = 8.0085662595372944372e-17;
const CP = 9.61796693925975554329e-01;
const CP_H = 9.61796700954437255859e-01;
const CP_L = -7.02846165095275826516e-09;
const IVLN2 = 1.44269504088896338700e+00;
const IVLN2_H = 1.44269502162933349609e+00;
const IVLN2_L = 1.92596299112661746887e-08;
const TWOM1022 = 2.2250738585072014e-308;

const scalbnSubnormal = (x, n) => {
	if(n < -1022){
		x *= TWOM1022;
		n += 1022;
		if(n < -1022) n = -1022;
	}
	return x * fromWords(0x3ff00000 + (n << 20), 0);
};

export function pow(x, y){
	x = +x;
	y = +y;
	const hx = getHigh(x);
	const lx = getLow(x);
	const hy = getHigh(y);
	const ly = getLow(y);
	let ix = hx & 0x7fffffff;
	const iy = hy & 0x7fffffff;

	if((iy | ly) === 0) return 1;
	if(ix > 0x7ff00000 || (ix === 0x7ff00000 && lx !== 0) || iy > 0x7ff00000 || (iy === 0x7ff00000 && ly !== 0)) return NaN;

	// 0: y is not an integer, 1: y is an odd integer, 2: y is an even integer
	let yisint = 0;
	if(hx < 0){
		if(iy >= 0x43400000){
			yisint = 2;
		}else if(iy >= 0x3ff00000){
			const k = (iy >> 20) - 0x3ff;
			if(k > 20){
				const j = ly >>> (52 - k);
				if(((j << (52 - k)) >>> 0) === ly) yisint = 2 - (j & 1);
			}else if(ly === 0){
				const j = iy >> (20 - k);
				if((j << (20 - k)) === iy) yisint = 2 - (j & 1);
			}
		}
	}

	if(ly === 0){
		if(iy === 0x7ff00000){
			if(((ix - 0x3ff00000) | lx) === 0) return NaN;
			if(ix >= 0x3ff00000) return hy >= 0 ? y : 0;
			return hy < 0 ? -y : 0;
		}
		if(iy === 0x3ff00000) return hy < 0 ? 1 / x : x;
		if(hy === 0x40000000) return x * x;
		if(hy === 0x3fe00000 && hx >= 0) return Math.sqrt(x);
	}

	let ax = Math.abs(x);
	if(lx === 0 && (ix === 0x7ff00000 || ix === 0 || ix === 0x3ff00000)){
		let z = ax;
		if(hy < 0) z = 1 / z;
		if(hx < 0){
			if(((ix - 0x3ff00000) | yisint) === 0) z = NaN;
			else if(yisint === 1) z = -z;
		}
		return z;
	}

	let n = (hx >> 31) + 1;
	if((n | yisint) === 0) return NaN;
	const sign = (n | (yisint - 1)) === 0 ? -1 : 1;

	let t1, t2;
	if(iy > 0x41e00000){
		if(iy > 0x43f00000){
			if(ix <= 0x3fefffff) return hy < 0 ? Infinity : 0;
			if(ix >= 0x3ff00000) return hy > 0 ? Infinity : 0;
		}
		if(ix < 0x3fefffff) return hy < 0 ? sign * Infinity : sign * 0;
		if(ix > 0x3ff00000) return hy > 0 ? sign * Infinity : sign * 0;
		const t = ax - 1;
		const w = (t * t) * (0.5 - t * (0.3333333333333333333333 - t * 0.25));
		const u = IVLN2_H * t;
		const v = t * IVLN2_L - w * IVLN2;
		t1 = setLow(u + v, 0);
		t2 = v - (t1 - u);
	}else{
		n = 0;
		if(ix < 0x00100000){
			ax *= TWO53;
			n -= 53;
			ix = getHigh(ax);
		}
		n += (ix >> 20) - 0x3ff;
		const j = ix & 0x000fffff;
		ix = j | 0x3ff00000;
		let k;
		if(j <= 0x3988E){
			k = 0;
		}else if(j < 0xBB67A){
			k = 1;
		}else{
			k = 0;
			n += 1;
			ix -= 0x00100000;
		}
		ax = setHigh(ax, ix);

		let u = ax - BP[k];
		let v = 1 / (ax + BP[k]);
		const ss = u * v;
		const sHigh = setLow(ss, 0);
		let tHigh = fromWords(((ix >> 1) | 0x20000000) + 0x00080000 + (k << 18), 0);
		let tLow = ax - (tHigh - BP[k]);
		const sLow = v * ((u - sHigh * tHigh) - sHigh * tLow);
		let s2 = ss * ss;
		let r = s2 * s2 * (L1 + s2 * (L2 + s2 * (L3 + s2 * (L4 + s2 * (L5 + s2 * L6)))));
		r += sLow * (sHigh + ss);
		s2 = sHigh * sHigh;
		tHigh = setLow(3 + s2 + r, 0);
		tLow = r - ((tHigh - 3) - s2);
		u = sHigh * tHigh;
		v = sLow * tHigh + tLow * ss;
		const pHigh = setLow(u + v, 0);
		const pLow = v - (pHigh - u);
		const zHigh = CP_H * pHigh;
		const zLow = CP_L * pHigh + pLow * CP + DP_L[k];
		const t = n;
		t1 = setLow(((zHigh + zLow) + DP_H[k]) + t, 0);
		t2 = zLow - (((t1 - t) - DP_H[k]) - zHigh);
	}

	const y1 = setLow(y, 0);
	const pLow = (y - y1) * t1 + y * t2;
	let pHigh = y1 * t1;
	let z = pLow + pHigh;
	let j = getHigh(z);
	let i = getLow(z);
	if(j >= 0x40900000){
		if(((j - 0x40900000) | i) !== 0) return sign * Infinity;
		if(pLow + OVT > z - pHigh) return sign * Infinity;
	}else if((j & 0x7fffffff) >= 0x4090cc00){
		if(((j - 0xc090cc00) | i) !== 0) return sign * 0;
		if(pLow <= z - pHigh) return sign * 0;
	}

	i = j & 0x7fffffff;
	let k = (i >> 20) - 0x3ff;
	n = 0;
	if(i > 0x3fe00000){
		n = j + (0x00100000 >> (k + 1));
		k = ((n & 0x7fffffff) >> 20) - 0x3ff;
		const t = fromWords(n & ~(0x000fffff >> k), 0);
		n = ((n & 0x000fffff) | 0x00100000) >> (20 - k);
		if(j < 0) n = -n;
		pHigh -= t;
	}
	let t = setLow(pLow + pHigh, 0);
	const u = t * LG2_H;
	const v = (pLow - (t - pHigh)) * LG2_FULL + t * LG2_L;
	z = u + v;
	const w = v - (z - u);
	t = z * z;
	t1 = z - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
	const r = (z * t1) / (t1 - 2) - (w + z * w);
	z = 1 - (r - z);
	j = getHigh(z) + (n << 20);
	z = (j >> 20) <= 0 ? scalbnSubnormal(z, n) : setHigh(z, j);
	return sign * z;
}

const hypot2 = (x, y) => {
	let ha = getHigh(x) & 0x7fffffff;
	let hb = getHigh(y) & 0x7fffffff;
	let a, b;
	if(hb > ha){
		a = y;
		b = x;
		const j = ha;
		ha = hb;
		hb = j;
	}else{
		a = x;
		b = y;
	}
	a = setHigh(a, ha);
	b = setHigh(b, hb);
	if((ha - hb) > 0x3c00000) return a + b;
	let k = 0;
	if(ha > 0x5f300000){
		if(ha >= 0x7ff00000){
			let w = a + b;
			if(((ha & 0xfffff) | getLow(a)) === 0) w = a;
			if(((hb ^ 0x7ff00000) | getLow(b)) === 0) w = b;
			return w;
		}
		ha -= 0x25800000;
		hb -= 0x25800000;
		k += 600;
		a = setHigh(a, ha);
		b = setHigh(b, hb);
	}
	if(hb < 0x20b00000){
		if(hb <= 0x000fffff){
			if((hb | getLow(b)) === 0) return a;
			const t1 = fromWords(0x7fd00000, 0);
			b *= t1;
			a *= t1;
			k -= 1022;
			ha = getHigh(a);
			hb = getHigh(b);
			if(hb > ha){
				const t = a;
				a = b;
				b = t;
				const j = ha;
				ha = hb;
				hb = j;
			}
		}else{
			ha += 0x25800000;
			hb += 0x25800000;
			k -= 600;
			a = setHigh(a, ha);
			b = setHigh(b, hb);
		}
	}
	let w = a - b;
	if(w > b){
		const t1 = fromWords(ha, 0);
		const t2 = a - t1;
		w = Math.sqrt(t1 * t1 - (b * (-b) - t2 * (a + t1)));
	}else{
		a = a + a;
		const y1 = fromWords(hb, 0);
		const y2 = b - y1;
		const t1 = fromWords(ha + 0x00100000, 0);
		const t2 = a - t1;
		w = Math.sqrt(t1 * y1 - (w * (-w) - (t1 * y2 + t2 * b)));
	}
	return k !== 0 ? fromWords(0x3ff00000 + (k << 20), 0) * w : w;
};

export function hypot(x, y){
	const count = arguments.length;
	if(count === 2) return hypot2(+x, +y);
	if(count === 0) return 0;
	if(count === 1) return Math.abs(+x);
	// Scaled sum of squares: only exact IEEE operations, so still deterministic.
	const values = [];
	let largest = 0;
	let infinite = false;
	let nan = false;
	for(let i = 0; i < count; i++){
		const value = Math.abs(+arguments[i]);
		if(value === Infinity) infinite = true;
		else if(value !== value) nan = true;
		else if(value > largest) largest = value;
		values.push(value);
	}
	if(infinite) return Infinity;
	if(nan) return NaN;
	if(largest === 0) return 0;
	let sum = 0;
	for(const value of values){
		const scaled = value / largest;
		sum += scaled * scaled;
	}
	return Math.sqrt(sum) * largest;
}
